import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { APP_CONFIG } from '@/config/app.config';
import ApperIcon from '@/components/ApperIcon';
import ThemeToggle from '@/components/ThemeToggle';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { DEFS, DEF_BY_NO } from '@/canvas/defs/index';
import { familyOf } from '@/components/canvas/bits';

export const route = { path: '/', layout: 'public', access: 'public' };

const ARCS = [
  { name: 'Make the case', blurb: 'Day 1: choose your case and prove it is worth doing.', from: 0, to: 4 },
  { name: 'Make it buildable', blurb: 'Day 2: design what has to exist and the guardrails.', from: 5, to: 6 },
  { name: 'Make it happen', blurb: 'Day 2: people, order of work, and the pitch.', from: 7, to: 9 },
];

const STEPS = [
  {
    icon: 'UserPlus',
    title: 'Register once',
    body: 'Use your name, email and a password. Add your organisation the first time you sign in.',
  },
  {
    icon: 'PencilLine',
    title: 'Fill in step by step',
    body: 'Every answer saves as you type. Stop halfway, close the tab, and pick up exactly where you left off.',
  },
  {
    icon: 'FileDown',
    title: 'Confirm and take it away',
    body: 'Confirm each canvas, then download one clean A4 report to keep, share and present.',
  },
];

function guideSample() {
  const table = DEF_BY_NO[1]?.steps?.[0]?.blocks?.find((b) => b.type === 'table');
  const col = table?.columns?.[0];
  return col ? { label: col.label, guide: col.guide } : null;
}

export default function Home() {
  const user = useSelector((s) => s.user.user);
  const sample = guideSample();
  const signedIn = Boolean(user);
  const home = user?.profile === 'Facilitator_c' ? '/cohort' : '/dashboard';

  return (
    <div className="min-h-svh bg-background text-foreground">
      <header className="absolute inset-x-0 top-0 z-(--z-base) mx-auto flex max-w-6xl items-center justify-between gap-3 px-4 py-4 text-sidebar-foreground md:px-6">
        <Link to="/" className="flex items-center gap-2.5">
          <span className="flex size-9 items-center justify-center rounded-xl bg-sidebar-primary text-sidebar-primary-foreground">
            <ApperIcon name={APP_CONFIG.icon} size={20} />
          </span>
          <span className="font-heading text-lg font-semibold">{APP_CONFIG.name}</span>
        </Link>
        <div className="flex items-center gap-2">
          <ThemeToggle className="hidden border-sidebar-border sm:flex [&_button]:text-sidebar-foreground" />
          {signedIn ? (
            <Button asChild variant="secondary" size="sm">
              <Link to={home}>Open my canvases</Link>
            </Button>
          ) : (
            <>
              <Button asChild variant="ghost" size="sm" className="text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground">
                <Link to="/learner/login">Sign in</Link>
              </Button>
              <Button asChild size="sm" className="bg-highlight text-highlight-foreground hover:bg-highlight/90">
                <Link to="/learner/signup">Register</Link>
              </Button>
            </>
          )}
        </div>
      </header>

      <section className="relative overflow-hidden bg-sidebar text-sidebar-foreground">
        <div className="pointer-events-none absolute -right-24 -top-24 size-96 rounded-full bg-(color:--arc-case) opacity-25 blur-3xl" aria-hidden="true" />
        <div className="pointer-events-none absolute -bottom-32 left-1/4 size-96 rounded-full bg-(color:--arc-build) opacity-25 blur-3xl" aria-hidden="true" />
        <div className="pointer-events-none absolute -bottom-24 right-10 size-72 rounded-full bg-(color:--arc-happen) opacity-20 blur-3xl" aria-hidden="true" />
        <div className="relative mx-auto grid max-w-6xl gap-10 px-4 pb-16 pt-28 md:px-6 md:pb-24 md:pt-36 lg:grid-cols-[1.15fr_0.85fr] lg:items-center">
          <div className="space-y-6">
            <p className="inline-flex items-center gap-2 rounded-full border border-sidebar-border bg-sidebar-accent/60 px-3 py-1 text-xs font-medium">
              <ApperIcon name="GraduationCap" size={14} className="text-highlight" />
              AI-Powered Customer Experience, a two-day executive course
            </p>
            <h1 className="font-heading text-4xl font-semibold leading-[1.08] text-balance md:text-6xl">
              Nine canvases and one Case Card. <span className="text-highlight">Yours to take away.</span>
            </h1>
            <p className="max-w-xl text-lg leading-relaxed opacity-85">
              Fill in your canvases on screen instead of on paper. Each one guides you, calculates for you, and feeds the next. When you are done, download one report you can present.
            </p>
            <div className="flex flex-wrap items-center gap-3">
              {signedIn ? (
                <Button asChild size="lg" className="bg-highlight text-highlight-foreground hover:bg-highlight/90">
                  <Link to={home}>
                    Open my canvases <ApperIcon name="ArrowRight" size={18} />
                  </Link>
                </Button>
              ) : (
                <>
                  <Button asChild size="lg" className="bg-highlight text-highlight-foreground hover:bg-highlight/90">
                    <Link to="/learner/signup">
                      Register to start <ApperIcon name="ArrowRight" size={18} />
                    </Link>
                  </Button>
                  <Button
                    asChild
                    size="lg"
                    variant="outline"
                    className="border-sidebar-border bg-transparent text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                  >
                    <Link to="/learner/login">I already have an account</Link>
                  </Button>
                </>
              )}
            </div>
            <ul className="flex flex-wrap gap-x-6 gap-y-2 text-sm opacity-85">
              <li className="flex items-center gap-1.5">
                <ApperIcon name="CloudCheck" size={16} className="text-highlight" /> Saves as you type
              </li>
              <li className="flex items-center gap-1.5">
                <ApperIcon name="CircleHelp" size={16} className="text-highlight" /> A guide at every field
              </li>
              <li className="flex items-center gap-1.5">
                <ApperIcon name="Calculator" size={16} className="text-highlight" /> Calculates downstream for you
              </li>
            </ul>
          </div>

          {sample && (
            <div className="relative">
              <div className="space-y-3 rounded-2xl border border-sidebar-border bg-card p-4 text-card-foreground shadow-lg md:p-5">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Canvas 1: Maturity Snapshot</p>
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm font-medium">
                    {sample.label} <span className="text-destructive">*</span>
                  </p>
                  <span className="inline-flex h-6 items-center gap-1 rounded-full bg-primary px-1.5 text-xs font-medium text-primary-foreground">
                    <ApperIcon name="CircleHelp" size={14} /> Hide guide
                  </span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {['1', '2', '3', '4', '5'].map((n) => (
                    <span
                      key={n}
                      className={cn(
                        'inline-flex h-9 min-w-10 items-center justify-center rounded-lg border px-3 text-sm font-medium',
                        n === '2' ? 'border-primary bg-primary text-primary-foreground' : 'border-input',
                      )}
                    >
                      {n}
                    </span>
                  ))}
                </div>
                <div className="space-y-2.5 rounded-xl border border-primary/25 bg-accent/70 p-3.5 text-sm">
                  <p className="text-xs font-semibold uppercase tracking-wide text-primary">Guide: {sample.label}</p>
                  <p>
                    <span className="font-semibold">How to fill it in. </span>
                    {sample.guide?.how}
                  </p>
                  <p>
                    <span className="font-semibold">NorthStar example. </span>
                    {sample.guide?.example}
                  </p>
                  {sample.guide?.trap && (
                    <p className="rounded-lg bg-warning-muted p-2 text-warning">
                      <span className="font-semibold">Watch out. </span>
                      {sample.guide.trap}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      <section className="mx-auto max-w-6xl space-y-8 px-4 py-14 md:px-6 md:py-20">
        <div className="max-w-2xl space-y-2">
          <h2 className="font-heading text-3xl font-semibold text-balance">Less paper, more thinking</h2>
          <p className="text-muted-foreground">
            You keep working in the same order as the course. Come back whenever you like, change a number, and every canvas after it updates.
          </p>
        </div>
        <ol className="grid gap-4 md:grid-cols-3">
          {STEPS.map((s, i) => (
            <li key={s.title} className="space-y-3 rounded-2xl border border-border bg-card p-5 shadow-xs">
              <div className="flex items-center gap-3">
                <span className="flex size-10 items-center justify-center rounded-xl bg-primary text-primary-foreground">
                  <ApperIcon name={s.icon} size={20} />
                </span>
                <span className="font-heading text-sm font-semibold text-muted-foreground">Step {i + 1}</span>
              </div>
              <h3 className="font-heading text-lg font-semibold">{s.title}</h3>
              <p className="text-sm leading-relaxed text-muted-foreground">{s.body}</p>
            </li>
          ))}
        </ol>
      </section>

      <section className="bg-muted/50 py-14 md:py-20">
        <div className="mx-auto max-w-6xl space-y-8 px-4 md:px-6">
          <div className="max-w-2xl space-y-2">
            <h2 className="font-heading text-3xl font-semibold text-balance">The ten, in three arcs</h2>
            <p className="text-muted-foreground">Start with your own Case Card. Each canvas answers one question a sceptic will ask.</p>
          </div>
          <div className="grid gap-5 lg:grid-cols-3">
            {ARCS.map((arc) => {
              const fam = familyOf(DEF_BY_NO[arc.from]);
              return (
                <div key={arc.name} className={cn('space-y-4 rounded-2xl border border-t-4 border-border bg-card p-5 shadow-xs', fam.edgeTop)}>
                  <div>
                    <h3 className={cn('font-heading text-lg font-semibold', fam.ink)}>{arc.name}</h3>
                    <p className="text-sm text-muted-foreground">{arc.blurb}</p>
                  </div>
                  <ul className="space-y-3">
                    {DEFS.filter((d) => d.no >= arc.from && d.no <= arc.to).map((d) => (
                      <li key={d.no} className="flex gap-3">
                        <span className={cn('flex size-8 shrink-0 items-center justify-center rounded-lg font-heading text-sm font-semibold text-white', fam.solid)}>
                          {d.no === 0 ? <ApperIcon name="IdCard" size={16} /> : d.no}
                        </span>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold leading-tight">{d.no === 0 ? 'Case Card' : d.title}</p>
                          <p className="text-xs leading-snug text-muted-foreground">{d.question}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-14 md:px-6 md:py-20">
        <div className="flex flex-col items-start justify-between gap-6 rounded-3xl bg-sidebar p-8 text-sidebar-foreground md:flex-row md:items-center md:p-10">
          <div className="max-w-xl space-y-2">
            <h2 className="font-heading text-2xl font-semibold text-balance md:text-3xl">Ready for your Case Card?</h2>
            <p className="opacity-85">It takes about 45 minutes before Day 1. You can stop and come back at any time.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button asChild size="lg" className="bg-highlight text-highlight-foreground hover:bg-highlight/90">
              <Link to={signedIn ? home : '/learner/signup'}>{signedIn ? 'Open my canvases' : 'Register to start'}</Link>
            </Button>
          </div>
        </div>
      </section>

      <footer className="border-t border-border">
        <div className="mx-auto flex max-w-6xl flex-col gap-3 px-4 py-6 text-sm text-muted-foreground md:flex-row md:items-center md:justify-between md:px-6">
          <p>AICX: AI-Powered Customer Experience. A two-day course for executives.</p>
          <p>
            Running the workshop?{' '}
            <Link to="/facilitator/login" className="font-medium text-primary hover:underline">
              Facilitator sign in
            </Link>
          </p>
        </div>
      </footer>
    </div>
  );
}
